import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from '../api/client';
import { listAllCompanies, listAllEmployees } from '../api/directory';
import {
  checkInVisitor,
  checkOutVisitor,
  deleteVisitor,
  listVisitors,
  setVisitorDeparture,
  type Visitor,
} from '../api/operations';
import { ClickableRow, RowTitle } from '../components/ClickableRow';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toasts';
import { useAuth } from '../auth/AuthProvider';
import { Icon } from '../lib/icons';
import { dateLabel, timeLabel } from '../lib/calendar';

const PAGE_SIZE = 50;

type DateMode = 'single' | 'range';

interface Filters {
  q: string;
  mode: DateMode;
  date: string;
  from: string;
  to: string;
  company: string;
}

const EMPTY_FILTERS: Filters = {
  q: '',
  mode: 'single',
  date: '',
  from: '',
  to: '',
  company: 'all',
};

/**
 * FEAT-008 — Visitors.
 *
 * The filter bar is the prototype's four controls, but the filtering itself is
 * server-side (BR-058). Narrowing in the client would mean the browser had
 * already been sent every row it was hiding.
 */
export function VisitorsPage(): ReactElement {
  const { t, i18n } = useTranslation();
  const { notify } = useToast();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [deleting, setDeleting] = useState<Visitor | null>(null);
  const [viewing, setViewing] = useState<Visitor | null>(null);

  // Only the bounds for the active mode are sent, so switching back to a single
  // date does not silently keep a range applied.
  const query = useQuery({
    queryKey: ['visitors', filters],
    queryFn: () =>
      listVisitors({
        page: 1,
        pageSize: PAGE_SIZE,
        ...(filters.q ? { q: filters.q } : {}),
        ...(filters.mode === 'single' && filters.date ? { date: filters.date } : {}),
        ...(filters.mode === 'range' && filters.from ? { from: filters.from } : {}),
        ...(filters.mode === 'range' && filters.to ? { to: filters.to } : {}),
        ...(filters.company !== 'all' ? { company: filters.company } : {}),
      }),
  });

  const companies = useQuery({ queryKey: ['companies', 'all'], queryFn: listAllCompanies });

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['visitors'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const set = (patch: Partial<Filters>): void => setFilters((f) => ({ ...f, ...patch }));

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">{t('nav_ops')}</div>
          <h1>{t('visitors')}</h1>
          <div className="lead">
            <b className="num">{query.data?.onsite ?? 0}</b> {t('vs_onsite')}
          </div>
        </div>
        <button type="button" className="btn primary" onClick={() => setIsCheckingIn(true)}>
          <Icon name="plus" />
          {t('vs_new')}
        </button>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{t('visitors')}</h2>
          <span className="hint">{query.data?.total ?? 0}</span>
        </div>

        <div className="filterbar">
          <div className="fb-item">
            <label htmlFor="vs-q">{t('vs_search')}</label>
            <input
              id="vs-q"
              className="fb-input fb-search"
              type="search"
              autoComplete="off"
              placeholder={t('vs_search_ph')}
              value={filters.q}
              onChange={(e) => set({ q: e.target.value })}
            />
          </div>

          <div className="fb-item">
            <span className="lbl">{t('vs_date')}</span>
            <div className="tabs" role="group" aria-label={t('vs_date')}>
              {(['single', 'range'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={filters.mode === mode ? 'tab on' : 'tab'}
                  aria-pressed={filters.mode === mode}
                  onClick={() => set({ mode })}
                >
                  {t(mode === 'single' ? 'vs_single_date' : 'vs_date_range')}
                </button>
              ))}
            </div>
          </div>

          {filters.mode === 'single' ? (
            <div className="fb-item">
              <label htmlFor="vs-d">{t('vs_on_date')}</label>
              <input
                id="vs-d"
                className="fb-input"
                type="date"
                value={filters.date}
                onChange={(e) => set({ date: e.target.value })}
              />
            </div>
          ) : (
            <>
              <div className="fb-item">
                <label htmlFor="vs-from">{t('vs_from')}</label>
                <input
                  id="vs-from"
                  className="fb-input"
                  type="date"
                  value={filters.from}
                  onChange={(e) => set({ from: e.target.value })}
                />
              </div>
              <div className="fb-item">
                <label htmlFor="vs-to">{t('vs_to')}</label>
                <input
                  id="vs-to"
                  className="fb-input"
                  type="date"
                  value={filters.to}
                  onChange={(e) => set({ to: e.target.value })}
                />
              </div>
            </>
          )}

          <div className="fb-item">
            <label htmlFor="vs-co">{t('vs_company')}</label>
            <select
              id="vs-co"
              className="fb-input"
              value={filters.company}
              onChange={(e) => set({ company: e.target.value })}
            >
              <option value="all">{t('vs_all_companies')}</option>
              {companies.data?.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          <div className="fb-actions">
            <button type="button" className="btn sm" onClick={() => setFilters(EMPTY_FILTERS)}>
              <Icon name="refresh" />
              {t('vs_clear')}
            </button>
          </div>
        </div>

        {query.isPending ? (
          <div className="empty" role="status" aria-live="polite">
            {t('loading')}
          </div>
        ) : query.error ? (
          <div className="empty" role="alert">
            {query.error instanceof ApiClientError ? query.error.message : t('login_network_error')}
          </div>
        ) : query.data && query.data.items.length === 0 ? (
          // A filter combination matching nothing is an empty state, not an error.
          <div className="empty">{t('vs_no_match')}</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('vs_name')}</th>
                  <th scope="col">{t('vs_host')}</th>
                  <th scope="col">{t('vs_reason')}</th>
                  <th scope="col">{t('vs_arrival')}</th>
                  <th scope="col">{t('status')}</th>
                  <th scope="col">
                    <span className="sr-only">{t('actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data?.items.map((visitor) => (
                  <VisitorRow
                    key={visitor.id}
                    visitor={visitor}
                    locale={i18n.language}
                    canDelete={can('Visitors.Delete')}
                    onCheckedOut={() => {
                      refresh();
                      notify(t('vs_checked_out'));
                    }}
                    onDelete={() => setDeleting(visitor)}
                    onOpen={() => setViewing(visitor)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewing ? (
        <VisitorDetailDialog
          visitor={viewing}
          canManage={can('Visitors.Manage')}
          onClose={() => setViewing(null)}
          onChanged={(updated) => {
            setViewing(updated);
            refresh();
          }}
        />
      ) : null}

      {isCheckingIn ? (
        <CheckInDialog
          onClose={() => setIsCheckingIn(false)}
          onSaved={(name) => {
            setIsCheckingIn(false);
            refresh();
            notify(t('vs_registered', { name }));
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteVisitorDialog
          visitor={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            refresh();
            notify(t('vs_deleted'));
          }}
        />
      ) : null}
    </>
  );
}

function VisitorRow({
  visitor,
  locale,
  canDelete,
  onCheckedOut,
  onDelete,
  onOpen,
}: {
  visitor: Visitor;
  locale: string;
  canDelete: boolean;
  onCheckedOut: () => void;
  onDelete: () => void;
  onOpen: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const checkOut = useMutation({
    mutationFn: () => checkOutVisitor(visitor.id),
    onSuccess: onCheckedOut,
    onError: (error) => {
      if (error instanceof ApiClientError) notify(error.message, 'bad');
    },
  });

  return (
    <ClickableRow onOpen={onOpen}>
      <td>
        <RowTitle onOpen={onOpen}>{visitor.name}</RowTitle>
        {/* Pinned LTR: a phone number is digits and reads the same in both languages. */}
        {visitor.phone ? (
          <div className="t-sub" dir="ltr">
            {visitor.phone}
          </div>
        ) : null}
      </td>
      <td>
        {visitor.host_name}
        <div className="t-sub">{visitor.host_company_name}</div>
      </td>
      <td>{visitor.reason ?? '—'}</td>
      <td className="num">
        {dateLabel(visitor.date, locale)}
        <div className="t-sub">
          <bdi>
            {timeLabel(visitor.arrived_at, locale)}
            {visitor.departed_at ? ` – ${timeLabel(visitor.departed_at, locale)}` : ''}
          </bdi>
        </div>
      </td>
      <td>
        <span className={visitor.status === 'onsite' ? 'badge b-progress' : 'badge b-neutral'}>
          {t(visitor.status === 'onsite' ? 'vs_onsite' : 'vs_left')}
        </span>
      </td>
      <td>
        <div className="row-actions">
          {/* BR-022 — only an on-site visitor can be checked out. */}
          {visitor.status === 'onsite' ? (
            <button
              type="button"
              className="btn sm"
              onClick={() => checkOut.mutate()}
              disabled={checkOut.isPending}
            >
              {t('act_checkout')}
            </button>
          ) : null}

          {canDelete ? (
            <button
              type="button"
              className="btn sm danger"
              onClick={onDelete}
              aria-label={`${t('delete')} ${visitor.name}`}
            >
              <Icon name="trash" />
            </button>
          ) : null}
        </div>
      </td>
    </ClickableRow>
  );
}

function CheckInDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (name: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [companyId, setCompanyId] = useState('boud');
  const [hostId, setHostId] = useState('');
  const [reason, setReason] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const companies = useQuery({ queryKey: ['companies', 'all'], queryFn: listAllCompanies });
  const people = useQuery({ queryKey: ['employees', 'all'], queryFn: listAllEmployees });

  /**
   * BR-021 — the host list is scoped to the selected company, and changing the
   * company clears a selection that no longer belongs to it.
   *
   * The clearing matters: leaving a stale host selected would send a request the
   * server correctly refuses, and the user would see a validation error about a
   * field they never touched.
   */
  const hosts = useMemo(
    () =>
      (people.data ?? []).filter(
        (person) => person.employer_id === companyId && person.status !== 'resigned',
      ),
    [people.data, companyId],
  );

  const selectedHost = hosts.some((h) => h.id === hostId) ? hostId : '';

  const mutation = useMutation({
    mutationFn: () =>
      checkInVisitor({
        name,
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        host_company_id: companyId,
        host_employee_id: selectedHost,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: (visitor) => onSaved(visitor.name),
    onError: (error) => {
      if (error instanceof ApiClientError) {
        setFieldErrors(error.fieldErrors);
        notify(error.message, 'bad');
      }
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFieldErrors({});
    mutation.mutate();
  }

  return (
    <Modal
      title={t('vs_new')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="submit"
            form="visitor-form"
            className="btn primary"
            disabled={mutation.isPending || !selectedHost}
          >
            {mutation.isPending ? t('saving') : t('vs_checkin')}
          </button>
        </>
      }
    >
      <form id="visitor-form" onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="vs-name">{t('vs_name')}</label>
          <input
            id="vs-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldErrors.name ? 'bad' : ''}
            aria-invalid={Boolean(fieldErrors.name)}
          />
          {fieldErrors.name ? (
            <div className="field-error" role="alert">
              {fieldErrors.name}
            </div>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="vs-phone">
            {t('vs_phone')}
            <span className="opt"> {t('optional')}</span>
          </label>
          <input id="vs-phone" dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="vs-company">{t('vs_company')}</label>
          <select
            id="vs-company"
            value={companyId}
            onChange={(e) => {
              setCompanyId(e.target.value);
              // The old host belongs to the old company.
              setHostId('');
            }}
          >
            {companies.data?.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="vs-host">{t('vs_host')}</label>
          <select
            id="vs-host"
            required
            aria-required="true"
            value={selectedHost}
            onChange={(e) => setHostId(e.target.value)}
            className={fieldErrors.host_employee_id ? 'bad' : ''}
            aria-invalid={Boolean(fieldErrors.host_employee_id)}
          >
            <option value="">{t('vs_pick_host')}</option>
            {hosts.map((host) => (
              <option key={host.id} value={host.id}>
                {host.name}
              </option>
            ))}
          </select>
          {fieldErrors.host_employee_id ? (
            <div className="field-error" role="alert">
              {fieldErrors.host_employee_id}
            </div>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="vs-reason">
            {t('vs_reason')}
            <span className="opt"> {t('optional')}</span>
          </label>
          <input id="vs-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </form>
    </Modal>
  );
}

function DeleteVisitorDialog({
  visitor,
  onClose,
  onDeleted,
}: {
  visitor: Visitor;
  onClose: () => void;
  onDeleted: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const mutation = useMutation({
    mutationFn: () => deleteVisitor(visitor.id),
    onSuccess: onDeleted,
    onError: (error) => {
      if (error instanceof ApiClientError) notify(error.message, 'bad');
    },
  });

  return (
    <Modal
      title={t('vs_delete')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className="btn danger"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('saving') : t('confirm_del')}
          </button>
        </>
      }
    >
      <p>{t('vs_delete_q', { name: visitor.name })}</p>
    </Modal>
  );
}

/**
 * A read-only visitor record, matching the prototype's `detailVis`.
 *
 * Almost read-only on purpose: check-out and delete stay in the row, because a
 * dialog opened by clicking anywhere on that row is the wrong place for a
 * destructive control.
 *
 * The one exception is the departure time, which Reception routinely records
 * late — a visitor leaves at four and is checked out at half past five, when
 * somebody next looks at the screen. Correcting it changes no other fact about
 * the visit, and the route it calls cannot change one.
 */
function VisitorDetailDialog({
  visitor,
  canManage,
  onClose,
  onChanged,
}: {
  visitor: Visitor;
  canManage: boolean;
  onClose: () => void;
  onChanged: (updated: Visitor) => void;
}): ReactElement {
  const { t, i18n } = useTranslation();
  const { notify } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => setVisitorDeparture(visitor.id, new Date(value).toISOString()),
    onSuccess: (updated) => {
      setIsEditing(false);
      setFieldError(null);
      notify(t('vs_departure_saved'));
      onChanged(updated);
    },
    onError: (error) => {
      if (error instanceof ApiClientError) {
        /*
         * The server names the field, and both of its refusals land on this one
         * input — before they arrived, and in the future. Shown against the
         * input rather than as a toast, which is where somebody correcting a
         * time is looking.
         */
        setFieldError(error.fieldErrors.departed_at ?? error.message);
      }
    },
  });

  /** `datetime-local` wants the building's wall clock, not an ISO instant. */
  function localValueOf(iso: string): string {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Riyadh',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date(iso));
    const at = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
    return `${at('year')}-${at('month')}-${at('day')}T${at('hour')}:${at('minute')}`;
  }

  return (
    <Modal
      title={visitor.name}
      onClose={onClose}
      footer={
        <button type="button" className="btn ghost" onClick={onClose}>
          {t('close')}
        </button>
      }
    >
      <dl className="dl">
        {visitor.phone ? (
          <>
            <dt>{t('vs_phone')}</dt>
            {/* Pinned LTR: digits read the same in both languages. */}
            <dd dir="ltr">{visitor.phone}</dd>
          </>
        ) : null}

        <dt>{t('vs_host')}</dt>
        <dd>{visitor.host_name}</dd>

        <dt>{t('vs_company')}</dt>
        <dd>{visitor.host_company_name}</dd>

        <dt>{t('vs_reason')}</dt>
        <dd>{visitor.reason ?? '—'}</dd>

        <dt>{t('vs_arrival')}</dt>
        <dd>
          {dateLabel(visitor.date, i18n.language)} ·{' '}
          <bdi>{timeLabel(visitor.arrived_at, i18n.language)}</bdi>
        </dd>

        <dt>{t('vs_checkout')}</dt>
        <dd>
          {isEditing ? (
            <>
              <input
                type="datetime-local"
                className="in"
                value={value}
                aria-label={t('vs_checkout')}
                aria-invalid={fieldError ? true : undefined}
                onChange={(event) => {
                  setValue(event.target.value);
                  setFieldError(null);
                }}
              />
              {fieldError ? (
                <div className="err" role="alert">
                  {fieldError}
                </div>
              ) : null}
              <div className="row-actions">
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => {
                    setIsEditing(false);
                    setFieldError(null);
                  }}
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  className="btn primary sm"
                  onClick={() => save.mutate()}
                  disabled={save.isPending || !value}
                >
                  {save.isPending ? t('saving') : t('save')}
                </button>
              </div>
            </>
          ) : (
            <>
              {visitor.departed_at ? (
                <bdi>{timeLabel(visitor.departed_at, i18n.language)}</bdi>
              ) : (
                '—'
              )}
              {/*
                Only once they have actually left. For somebody still on site the
                operation is check-out, which records the time and moves the
                status together — offering an edit here would suggest you could
                give a departure to a visitor who is still in the building.
              */}
              {canManage && visitor.departed_at ? (
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => {
                    setValue(localValueOf(visitor.departed_at!));
                    setIsEditing(true);
                  }}
                >
                  {t('vs_edit_departure')}
                </button>
              ) : null}
            </>
          )}
        </dd>

        <dt>{t('status')}</dt>
        <dd>
          <span className={visitor.status === 'onsite' ? 'badge b-progress' : 'badge b-neutral'}>
            {t(visitor.status === 'onsite' ? 'vs_onsite' : 'vs_left')}
          </span>
        </dd>
      </dl>
    </Modal>
  );
}
